import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { IPageDataV2, IPageDataV3, IPageDataV4 } from '@df/catalog/cms/cms-model.interface';
import { SocialService } from '@df/ui/social/social.service';
import { copyToClipboard } from '@df/util/fn/copy-to-clipboard';
import { ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { NICmsContentField } from '@icms/core/content/interfaces/icms-content-field.interfaces';
import { ICmsPageType } from '@icms/core/content/page/icms-page-type.decorator';
import { ICmsDataChangeService } from '@icms/core/data/change/icms-data-change.service';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { LocalPage } from 'projects/app-default/src/app/catalog/page/local/local-page';
import { PageViewComponent } from 'projects/app-default/src/app/catalog/page/view/page-view.component';
import { NBlogCategoryPageType } from '../category-view/cms-blog-page-category-view.component';
import { BLOG_TAB, NBlogRootPageType } from '../root-view/cms-blog-page-root-view.component';

declare module '@df/catalog/cms/cms-model.interface' {
    export interface IPageDataV3 {
        components: NICmsContentComponent.IData[];
    }
}

export interface ICmsAdditionalMeta {
    [key: string]: any;
}

export namespace NBlogPostPageType {
    export const typeId = 'blog-post';

    export interface ITypeData {
        tags?: number[];
        author?: string;
        date?: Date;
        readingTime?: string;
    }
}

@ICmsPageType({
    id: NBlogPostPageType.typeId,
    title: 'Blog post',
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'typeData.author',
            title: 'Post author',
            tab: BLOG_TAB
        },
        {
            type: NICmsContentField.EFieldType.date,
            key: 'typeData.date',
            title: 'Post date',
            tab: BLOG_TAB
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'typeData.readingTime',
            title: 'Reading time',
            default: '3m',
            tab: BLOG_TAB
        },
        <NICmsContentField.IRelationField<IPageDataV4, ICmsPage>>{
            key: 'typeData.tags',
            type: NICmsContentField.EFieldType.relation,
            title: 'Additional blog tags',
            options: {
                title: 'Select page',
                type: 'page',
                multiple: true,
                filter: (model: ICmsPage, data: IPageDataV4) => model?.rawData?.pageType === NBlogCategoryPageType.typeId
            },
            tab: BLOG_TAB
        }
    ],
    /**
     * Only allow parent page to be blog-category
     */
    parentFilter: page => {
        return page.rawData?.pageType === NBlogCategoryPageType.typeId;
    }
})
@Component({
    selector: 'cms-blog-page-post-view',
    templateUrl: './cms-blog-page-post-view.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsBlogPagePostViewComponent extends PageViewComponent implements OnChanges {
    /**
     * Blog tag categories
     */
    tagCategories: LocalPage<IPageDataV2>[] = [];

    @Input({ required: true })
    data!: IPageDataV3;

    @Input()
    @ICmsContent({
        watchDepth: 1
    })
    typeData?: NBlogPostPageType.ITypeData;

    constructor(
        iCmsDataChangeService: ICmsDataChangeService,
        private socialService: SocialService
    ) {
        super(iCmsDataChangeService);
    }

    fbShare() {
        if (this.model) {
            return this.socialService.fbShare(this.model);
        }
    }

    twShare() {
        if (this.model) {
            return this.socialService.twShare(this.model);
        }
    }

    pinShare() {
        if (this.model) {
            return this.socialService.pinShare(this.model);
        }
    }

    copyUrlToClipboard() {
        copyToClipboard(window.location.href);
    }

    protected get image(): string {
        return this.data.image || '';
    }

    protected get components(): NICmsContentComponent.IData[] {
        return this.data.components || [];
    }

    protected get name(): string {
        return this.data.name || '';
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['model']) {
            this.tagCategories = (this.model as LocalPage<IPageDataV3>)?.getBlogPostCategories()?.slice(0, 3) || [];
            super.ngOnChanges(changes);
        }
    }

    get backToRootUrl(): string | undefined {
        return this.model?.parent?.parent?.data?.pageType === NBlogRootPageType.typeId
            ? this.model.parent.parent.url
            : this.model?.parent?.url;
    }
}
